import { DatagridChangeOpts } from "@dashboard/components/Datagrid/hooks/useDatagridChange";
import {
  ProductErrorCode,
  ProductErrorWithAttributesFragment,
  ProductFragment,
} from "@dashboard/graphql";
import { IntlShape } from "react-intl";
import { z } from "zod";

import { ProductCreateData } from "../components/ProductCreatePage";
import { ProductVariantCreateData } from "../components/ProductVariantCreatePage/form";
import { ProductVariantUpdateSubmitData } from "../components/ProductVariantPage/form";
import { getColumnChannel } from "../utils/datagrid";

export const validatePrice = (price: string) => price === "" || parseInt(price, 10) < 0;

export const validateCostPrice = (price: string) => price !== "" && parseInt(price, 10) < 0;

const toChannelPriceField = (id: string) => `${id}-channelListing-price`;
const createRequiredError = (
  field: string,
  message: string | null = null,
): ProductErrorWithAttributesFragment => ({
  __typename: "ProductError",
  code: ProductErrorCode.REQUIRED,
  field,
  message,
  attributes: [],
});

export const validateProductCreateData = (data?: ProductCreateData) => {
  let errors: ProductErrorWithAttributesFragment[] = [];

  if (!data) {
    return errors;
  }

  if (!data.productType) {
    errors = [...errors, createRequiredError("productType")];
  }

  if (!data.name) {
    errors = [...errors, createRequiredError("name")];
  }

  const { productType, channelListings } = data;

  if (!productType.hasVariants && channelListings) {
    const emptyPrices = data.channelListings
      .filter(channel => channel.price?.length === 0)
      .map(({ id }) => createRequiredError(toChannelPriceField(id)));

    errors = [...errors, ...emptyPrices];
  }

  return errors;
};

const channelListingValueSchema = z.object({
  price: z.number().or(z.string().min(1)),
});

const channelListingSchema = z
  .object({
    channelListings: z.array(
      z.object({
        value: channelListingValueSchema,
      }),
    ),
    variantName: z.string().min(1),
  })
  .partial();

export type ProductVariantType = ProductVariantCreateData | ProductVariantUpdateSubmitData;

const handleValidationError = (
  error: z.ZodIssue,
  data: ProductVariantType,
  defaultMessage: string,
) => {
  const defaultError = createRequiredError(error.path.join("-"), defaultMessage);

  switch (error.code) {
    case "too_small":
    case "invalid_union":
    case "invalid_type":
      if (error.path.includes("price") && error.path.includes("channelListings")) {
        // Due to the way the form was written
        // The path is in format "channelListing => {index} => price"
        const index = error.path[1] as number;
        const listing = data.channelListings[index];

        return createRequiredError(toChannelPriceField(listing.id), defaultMessage);
      }

      return defaultError;
    default:
      return defaultError;
  }
};

export const validateProductVariant = (data: ProductVariantType, intl: IntlShape) => {
  const result = channelListingSchema.safeParse(data);

  const defaultMessage = intl.formatMessage({
    defaultMessage: "This field cannot be blank",
    id: "8pVWve",
  });

  return result.success === true
    ? []
    : result.error.issues.map(error => handleValidationError(error, data, defaultMessage));
};

export interface VariantPriceValidationResult {
  variantId: string;
  variantName: string;
  channelId: string;
  channelName: string;
}

/**
 * Checks if there's a pending price change for a variant/channel in the datagrid changes.
 */
const hasPendingPriceChange = (
  variantIndex: number,
  channelId: string,
  variantChanges?: DatagridChangeOpts,
): boolean => {
  if (!variantChanges?.updates) {
    return false;
  }

  // Look for a price change for this variant and channel
  return variantChanges.updates.some(change => {
    if (change.row !== variantIndex) {
      return false;
    }

    const changeChannelId = getColumnChannel(change.column);

    if (changeChannelId !== channelId) {
      return false;
    }

    // Check if there's a valid price value (not null, not undefined, not empty)
    const value = change.data?.value;

    return value !== null && value !== undefined && value !== "";
  });
};

/**
 * Validates that all variants have prices set for all channels where the product is published.
 * Takes into account pending changes from the datagrid.
 * Returns a list of variants and channels that are missing prices.
 */
export const validateVariantPricesForChannels = (
  product: ProductFragment,
  variantChanges?: DatagridChangeOpts,
): VariantPriceValidationResult[] => {
  const errors: VariantPriceValidationResult[] = [];

  if (!product?.variants?.length || !product?.channelListings?.length) {
    return errors;
  }

  // Get all channel IDs where the product is published and available for purchase
  const publishedChannels = product.channelListings
    .filter(listing => listing.isPublished && listing.isAvailableForPurchase)
    .map(listing => ({
      id: listing.channel.id,
      name: listing.channel.name,
    }));

  if (publishedChannels.length === 0) {
    return errors;
  }

  // Check each variant for each published channel
  for (let variantIndex = 0; variantIndex < product.variants.length; variantIndex++) {
    const variant = product.variants[variantIndex];

    for (const channel of publishedChannels) {
      const variantChannelListing = variant.channelListings?.find(
        listing => listing.channel.id === channel.id,
      );

      const hasExistingPrice = variantChannelListing && variantChannelListing.price !== null;
      const hasPendingPrice = hasPendingPriceChange(variantIndex, channel.id, variantChanges);

      // If no existing price AND no pending price change, it's an error
      if (!hasExistingPrice && !hasPendingPrice) {
        errors.push({
          variantId: variant.id,
          variantName: variant.name || variant.sku || variant.id,
          channelId: channel.id,
          channelName: channel.name,
        });
      }
    }
  }

  return errors;
};
